<template>
  <SrpModal v-model:isVisible="isVisible" :size="props.isAuthenticated ? 'medium' : 'large'">
    <template #content>
      <!-- "Get started" form -->
      <div class="get-started-form" style="margin-bottom: 15px">
        <div class="get-started-form__header">
          <h1 class="global-h1 get-started-form__title">How will you use Shrpa?</h1>
          <div v-if="!enableCustomCollabFreeDest" class="get-started-form__subtitle">Custom Packages are currently available to Shrpa Customers</div>
        </div>

        <ul class="get-started-form__options-list">
          <!-- Option tile -->
          <li class="option-tile get-started-form__option-tile" v-for="(getStartedOption, index) in getStartedOptionsList" :key="index">
            <div class="option-tile__icon-wrap">
              <img
                class="option-tile__icon"
                :src="`/images/${getStartedOption.icon}.svg`"
                :alt="''"
                :style="{
                  width: getStartedOption.iconSize + 'px',
                  minWidth: getStartedOption.iconSize + 'px',
                  top: getStartedOption.iconTopShift + 'px',
                }"
              />
            </div>

            <div class="option-tile__text-wrap">
              <h4 class="global-h4 option-tile__title">
                {{ getStartedOption.title }}
              </h4>
              <h5 class="global-h5 option-tile__subtitle">
                {{ getStartedOption.subtitle }}
              </h5>
            </div>

            <SrpButton v-if="getStartedOption.ctaButtonLink" class="option-tile__cta-button" :href="getStartedOption.ctaButtonLink" target="_blank">
              {{ getStartedOption.ctaButtonText }}
            </SrpButton>

            <SrpButton v-else class="option-tile__cta-button" @click="() => getStartedOption.ctaButtonCallback?.()">
              {{ getStartedOption.ctaButtonText }}
            </SrpButton>
          </li>
          <!-- / Option tile -->
        </ul>
      </div>
      <!-- / "Get started" form -->
    </template>
  </SrpModal>
</template>

<script setup lang="ts">
import { ref, inject, computed } from "vue";
import FeatureFlags from "@/logic/FeatureFlags";

// Components
import SrpModal from "@components/ui/SrpModal.vue";
import SrpButton from "@components/ui/SrpButton.vue";

// Global variables & injections
const globalGetLink = inject("globalGetLink") as any;

const props = withDefaults(
  defineProps<{
    isAuthenticated: boolean;
    creatorId: string;
    packageId: string;
  }>(),
  {
    isAuthenticated: false,
    creatorId: "",
    packageId: "",
  }
);

const emit = defineEmits<{
  (e: "existingCustomerClick"): void;
  (e: "newDestinationClick", creatorId: string, packageId: string): void;
}>();

// Get started Options list ===================================================
interface GetStartedOption {
  icon: string;
  iconSize: number;
  iconTopShift?: number;
  title: string;
  subtitle?: string;
  ctaButtonText: string;
  ctaButtonCallback?: Function;
  ctaButtonLink?: string;
}

const enableCustomCollabFreeDest = FeatureFlags.isFlagActive("EnableCustomCollabFreeDest");
const getStartedOptionsList = computed<Array<GetStartedOption>>(() => {
  const optionsList: Array<GetStartedOption> = [
    {
      icon: "globeWithMapMarker",
      iconSize: 77,
      iconTopShift: -2,
      title: "Destination",
      subtitle: "interested in working with creators?",
      ctaButtonText: enableCustomCollabFreeDest ? "Get Started" : "Learn more",
      ctaButtonLink: enableCustomCollabFreeDest ? undefined : globalGetLink("ForCommunities"),
      ctaButtonCallback: enableCustomCollabFreeDest ? () => emit("newDestinationClick", props.creatorId, props.packageId) : undefined,
    },
    {
      icon: "bulbWithLight",
      iconSize: 100,
      iconTopShift: 2,
      title: "A Creator",
      subtitle: "interested in working with destinations?",
      ctaButtonText: "Learn more",
      ctaButtonLink: globalGetLink("ForCreators"),
    },
  ];

  if (!props.isAuthenticated) {
    const loginOption: GetStartedOption = {
      icon: "userSimple",
      iconSize: 75,
      iconTopShift: 0,
      title: "Existing Shrpa Customer?",
      ctaButtonText: "Log In",
      ctaButtonCallback: () => emit("existingCustomerClick"),
    };
    optionsList.unshift(loginOption);
  }

  return optionsList;
});

// Toggle visibility ==========================================================
const isVisible = ref<boolean>(false);

// Define expose ==============================================================
defineExpose({
  openModal: () => (isVisible.value = true),
  closeModal: () => (isVisible.value = false),
});
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";
@import "@/scss/variables.scss";

// Option tile ================================================================
.option-tile {
  padding: 20px 15px 25px;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: rgba(243, 243, 243, 1);

  &__icon-wrap {
    width: 100px;
    height: 100px;
    margin-bottom: 8px;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  &__icon {
    position: relative;
  }

  &__text-wrap {
    flex-grow: 1;
    margin-bottom: 15px;
  }

  &__title {
    margin-bottom: 1px;
    text-align: center;
  }

  &__subtitle {
    color: rgba(0, 0, 0, 0.7);
    text-align: center;
    font-weight: 500;
  }

  &__cta-button {
  }
}

// "Get started" form =========================================================
.get-started-form {
  &__header {
    margin-bottom: 24px;
  }

  &__title {
    margin-bottom: 3px;
  }

  &__subtitle {
    @include standardFont;
  }

  &__options-list {
    gap: $desktop-wide-grid-gap-width;
    padding: 0;
    margin: 0;
    display: flex;
    justify-content: center;
    list-style: none;
  }

  &__option-tile {
    flex-basis: 0;
    flex-grow: 1;
  }
}
</style>
